import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ESCAPE_STATUSES, type PublicSlotInfo } from "../../OnboardingAPI";
import { Field } from "../Fields";
import SlotUploader from "../SlotUploader";
import { Collapse } from "../calm";
import type { DroppedFiles } from "../dropFiles";
import { filesOfSlot } from "../onboardingModel";
import { ButtonTip } from "@/components/ui/button-tip";
import { StepBody, StepFooter, StepHeading } from "../StepCard";
import { numberedCount } from "../steps";
import { SLOT_TEXT_KEYS } from "../useOnboardingController";
import { useGlobalDrop } from "../useGlobalDrop";
import type { StepContext } from "./shared";

/** The notice about personal data: its own white card above the content (radius 16, 12 px), never inside it. */
export function PersonalDataNotice() {
  const { t } = useTranslation();
  return (
    <div role="note" data-testid="personal-data-notice" className="mb-3 flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
      <div>
        <p className="text-sm font-medium">{t("onboardingPage.notice.title")}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{t("onboardingPage.notice.text")}</p>
      </div>
    </div>
  );
}

export const slotTitle = (t: (key: string) => string, slot: PublicSlotInfo) => (SLOT_TEXT_KEYS.has(slot.slot) ? t(`onboardingPage.slots.${slot.slot}.title`) : slot.slot);
export const slotDescription = (t: (key: string) => string, slot: PublicSlotInfo) =>
  SLOT_TEXT_KEYS.has(slot.slot) ? t(`onboardingPage.slots.${slot.slot}.description`) : t("onboardingPage.slots.unknown.description");

/** Steps 2-5: one required slot, one step. A big drop zone, the files as cards, and "Weiter". */
export default function SlotStep({ ctx }: { ctx: StepContext }) {
  const { c } = ctx;
  const { t } = useTranslation();
  const view = c.view!;
  const slot = view.slots.find((candidate) => candidate.slot === ctx.step.slot)!;
  const stored = filesOfSlot(view.files, slot.slot);
  const items = c.uploads.filter((item) => item.slot === slot.slot);
  const running = items.some((item) => item.status === "waiting" || item.status === "uploading");
  const heading = t(`onboardingPage.headings.${slot.slot}`, { defaultValue: slotTitle(t, slot) });

  const onDrop = useCallback(
    (dropped: DroppedFiles) => {
      if (dropped.files.length) c.addFiles(slot, dropped.files);
      if (dropped.skipped) c.setSlotNotices((previous) => ({ ...previous, [slot.slot]: t("onboardingPage.drop.skippedFolder", { count: dropped.skipped }) }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [c.addFiles, slot.slot, t],
  );
  const dragging = useGlobalDrop(view.canEdit, slot.extensions, onDrop);

  const optional = !!ctx.step.optional;
  const canContinue = (optional || stored.length > 0 || ctx.skipped.has(slot.slot)) && !running;
  const answers = c.values!.answers;

  return (
    <>
      <StepBody>
        <StepHeading
          eyebrow={t("onboardingPage.nav.stepOf", { current: ctx.step.index, total: numberedCount(ctx.steps) })}
          title={heading}
          lead={slotDescription(t, slot)}
          focus={ctx.focus}
          extra={optional ? <span className="inline-flex rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{t("onboardingPage.slot.optional")}</span> : undefined}
        />
        <Collapse open={!!ctx.nudge}>
          <p role="status" className="rounded-xl bg-muted/60 px-4 py-3 text-sm">{ctx.nudge}</p>
        </Collapse>
        <PersonalDataNotice />
        <SlotUploader
          slot={slot}
          title={slotTitle(t, slot)}
          description={slotDescription(t, slot)}
          stored={stored}
          items={items}
          canEdit={view.canEdit}
          notice={c.slotNotices[slot.slot]}
          errorText={c.errorText}
          onPick={(files) => c.addFiles(slot, files)}
          onRemove={(file) => void c.removeStored(file)}
          preview={c.preview}
          onRetry={c.retryUpload}
          onDismiss={(item) => c.dropUpload(item.id)}
          onDismissNotice={() => c.setSlotNotices((previous) => ({ ...previous, [slot.slot]: undefined }))}
          dragging={dragging}
          hints={slot.slot === "assets"}
        />
        {slot.slot === "floorplans" && (
          <Field id="onboarding-floorplanVersionNote" label={t("onboardingPage.questions.floorplanVersionNote")} hint={t("onboardingPage.questions.floorplanVersionNoteHint")} error={c.problemText("floorplanVersionNote", c.values!.answers.floorplanVersionNote)}>
            <Input
              id="onboarding-floorplanVersionNote"
              value={c.values!.answers.floorplanVersionNote ?? ""}
              maxLength={500}
              aria-describedby="onboarding-floorplanVersionNote-hint"
              onChange={(event) => c.updateAnswers("floorplanVersionNote", event.target.value)}
            />
          </Field>
        )}
        {slot.slot === "escapePlans" && (
          <div className="space-y-4 rounded-2xl border border-border p-4" data-testid="escape-fields">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{t("onboardingPage.escape.statusLabel")}</legend>
              <div className="flex flex-wrap gap-2">
                {ESCAPE_STATUSES.map((status) => (
                  <label key={status} className="cursor-pointer">
                    <input type="radio" name="escapePlansStatus" value={status} checked={answers.escapePlansStatus === status} onChange={() => c.updateAnswers("escapePlansStatus", status)} className="peer sr-only" />
                    <span className="inline-flex h-10 items-center rounded-full border border-input px-4 text-sm transition-colors peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 max-sm:h-11">
                      {t(`onboardingPage.escape.status.${status}`)}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <Field id="onboarding-escapePlansNote" label={t("onboardingPage.escape.noteLabel")} error={c.problemText("escapePlansNote", answers.escapePlansNote)}>
              <Input id="onboarding-escapePlansNote" value={answers.escapePlansNote ?? ""} maxLength={300} onChange={(event) => c.updateAnswers("escapePlansNote", event.target.value)} />
            </Field>
          </div>
        )}
        <Collapse open={ctx.skipped.has(slot.slot) && stored.length === 0}>
          <p className="text-sm text-muted-foreground">{t("onboardingPage.slot.skippedNote")}</p>
        </Collapse>
      </StepBody>
      <StepFooter
        onBack={ctx.back}
        onNext={ctx.next}
        nextDisabled={!canContinue}
        nextReason={running ? t("onboardingPage.tips.waitUploads") : t("onboardingPage.tips.slotFirst")}
        hint={running ? t("onboardingPage.nav.waitUploads") : null}
        secondary={
          optional && stored.length === 0 && !running ? (
            <ButtonTip label={t("onboardingPage.tips.skip")}>
              <Button type="button" variant="outline" onClick={ctx.next}>
                {t("onboardingPage.nav.skip")}
              </Button>
            </ButtonTip>
          ) : stored.length === 0 && !running && !ctx.skipped.has(slot.slot) ? (
            <ButtonTip label={t("onboardingPage.tips.notNow")}>
              <button
                type="button"
                onClick={() => {
                  ctx.skip(slot.slot);
                  ctx.next();
                }}
                className="text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("onboardingPage.nav.notNow")}
              </button>
            </ButtonTip>
          ) : null
        }
      />
    </>
  );
}
